import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from '@doccareplus/i18n';
import { cn } from '@doccareplus/shared/lib/utils';
import { Button, ConfirmDialog, ThemeSegmentedControl } from '@doccareplus/ui';
import { logout, selectUser, useAppDispatch, useAppSelector } from '@doccareplus/kernel/application';
import { authApi } from '@doccareplus/kernel/infrastructure/api/auth.service';
import {
  applyActiveProfileHeader,
  clearPortalPatientData,
  PortalProfileSwitcher,
  profileName,
  usePortalHomeQuery,
} from '@/features/profiles';
import { PORTAL_TABS } from '../portalNavModel';

function userInitials(firstName: string, lastName: string) {
  return `${firstName[0] ?? ''}${lastName[0] ?? ''}`.toUpperCase() || 'U';
}

export function PortalDesktopShell() {
  const { t } = useTranslation('portal');
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const user = useAppSelector(selectUser);
  const home = usePortalHomeQuery();
  const [signOutConfirming, setSignOutConfirming] = useState(false);
  const activeTab = PORTAL_TABS.find((tab) =>
    tab.end ? location.pathname === tab.to : location.pathname.startsWith(tab.to)
  );
  const activeProfile = home.data?.activeProfile ?? null;
  const displayName = user ? `${user.firstName} ${user.lastName}`.trim() : t('appName', 'DocCare+');

  const handleLogout = async () => {
    try {
      await authApi.logout();
    } catch {
      // Best-effort server logout; the client session is cleared regardless.
    }
    applyActiveProfileHeader(null);
    await clearPortalPatientData(queryClient, { includeAccountData: true });
    dispatch(logout());
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <aside className="fixed inset-y-0 left-0 z-20 flex w-[264px] flex-col border-r border-[var(--border)] bg-[var(--surface)]">
        <div className="border-b border-[var(--border)] px-4 pb-3.5 pt-4">
          <div className="mb-3 flex items-center gap-2.5">
            <span className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-[var(--primary-weak)] text-[12px] font-bold text-[var(--primary)]">
              {user ? userInitials(user.firstName, user.lastName) : 'U'}
            </span>
            <div className="min-w-0">
              <div className="truncate text-[13.5px] font-bold">{displayName}</div>
              <div className="text-[11px] text-[var(--text-3)]">{t('accountLabel', 'Patient portal account')}</div>
            </div>
          </div>
          <PortalProfileSwitcher onFindProfiles={() => navigate('/profile')} />
        </div>

        <nav aria-label={t('navigation', 'Portal navigation')} className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-3 py-3.5">
          {PORTAL_TABS.map((tab) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.end}
              className={({ isActive }) => cn('nav-item min-h-10', isActive && 'active')}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                {tab.paths.map((d, i) => <path key={i} d={d} />)}
              </svg>
              <span>{t(tab.desktopLabelKey ?? tab.labelKey, tab.desktopFallback ?? tab.fallback)}</span>
            </NavLink>
          ))}

          {activeProfile && (
            <div className="mt-4 rounded-[12px] border border-[var(--border)] bg-[var(--surface-2)] p-3.5">
              <div className="truncate text-[12px] font-bold">{activeProfile.clinicName}</div>
              <div className="mt-1 text-[11.5px] leading-5 text-[var(--text-2)]">
                {t('clinicRecordHint', 'Your records on this screen are held by this clinic.')}
              </div>
            </div>
          )}
        </nav>

        <div className="border-t border-[var(--border)] p-3">
          <div className="mb-2"><ThemeSegmentedControl /></div>
          <Button variant="ghost" size="sm" fullWidth onClick={() => setSignOutConfirming(true)}>
            {t('signOut', 'Sign out')}
          </Button>
        </div>
      </aside>

      <div className="flex min-h-screen flex-col pl-[264px]">
        <header className="sticky top-0 z-10 flex min-h-[73px] items-center gap-5 border-b border-[var(--border)] bg-[var(--surface)] px-8 py-3.5">
          <div className="min-w-[200px] flex-1">
            <h1 className="text-[20px] font-bold tracking-[-0.01em]">
              {activeTab
                ? t(activeTab.desktopLabelKey ?? activeTab.labelKey, activeTab.desktopFallback ?? activeTab.fallback)
                : t('appName', 'DocCare+')}
            </h1>
            <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[12.5px] text-[var(--text-2)]">
              {activeProfile ? (
                <>
                  <span className="font-semibold text-[var(--text)]">{profileName(activeProfile)}</span>
                  <span className="text-[var(--text-3)]">·</span>
                  <span>{activeProfile.clinicName}</span>
                  <span className="rounded-full bg-[var(--primary-weak)] px-2 py-0.5 text-[11px] font-bold text-[var(--primary)]">
                    {t(`profile.relationship.${activeProfile.relationshipType.toLowerCase()}`, activeProfile.relationshipType)}
                  </span>
                </>
              ) : (
                <span>{t('profile.linkedAccountDescription', 'Linked patient profiles for this portal account')}</span>
              )}
            </div>
          </div>
          {activeProfile && !activeProfile.isArchived && location.pathname !== '/profile' && (
            <Button onClick={() => navigate('/appointments')}>
              <span aria-hidden="true">＋</span>
              {t('appointments.request', 'Request appointment')}
            </Button>
          )}
        </header>

        <main className="mx-auto w-full max-w-[1320px] flex-1 px-8 pb-12 pt-6">
          <Outlet />
        </main>
      </div>

      {signOutConfirming && (
        <ConfirmDialog
          title={t('signOutConfirmTitle', 'Sign out?')}
          description={t('signOutConfirmDescription', "You'll need to sign in again to access your portal account.")}
          confirmLabel={t('signOut', 'Sign out')}
          cancelLabel={t('staySignedIn', 'Stay signed in')}
          onConfirm={handleLogout}
          onCancel={() => setSignOutConfirming(false)}
        />
      )}
    </div>
  );
}
